import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Button, Text, Spinner, CheckBox } from "grommet";
import io from "socket.io-client";
import Popover from "react-popover";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { CommunicationService, NotificationService } from "@services";
import { presentToastErrorContent } from "@shared/Toast";
import { SecuredImage } from "@shared";
import { SERVER_URL, authHeader } from "@services/api";
import { useFetchHotel, useSetCurrentUser } from "@dashboard/redux/hooks";
import { renderAIHtml } from "@common/renderAIHtml";

import { isMessageLocked } from "../../../../common/messageVisibility";
import { getOfferCapabilities } from "../../../../common/offerCapabilities";
import { PackageUpgradeModal } from "../../components/OfferGate";
import MessagePhotoInput from "../../components/MessagePhotoInput";
import "./Conversation.scss";

export default function Conversation({
  bookingId,
  bookingIds,
  reservations = [],
  conversationId,
  guestName,
  phone,
  email,
  smsUnreachable = false,
  emailUnreachable = false,
  embedded = false,
  onMessageSent,
  onMessagesRead,
  externalMessage,
  initialCommunicationPolicy,
}) {
  const [currentMessage, setCurrentMessage] = useState("");
  const composerVersion = useRef(0);
  const [photo, setPhoto] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftHelpOpen, setDraftHelpOpen] = useState(false);
  const [draftError, setDraftError] = useState(false);
  const [draftContext, setDraftContext] = useState(null);
  const [upgrade, setUpgrade] = useState(false);
  const [forceSms, setForceSms] = useState(false);
  const [messages, setMessages] = useState([]);
  const [connectedToSocket, setConnectedToSocket] = useState(false);
  const [isGuestOnline, setIsGuestOnline] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingEarlierMessages, setIsLoadingEarlierMessages] =
    useState(false);
  const [isSending, setIsSending] = useState(false);
  const [internalReply, setInternalReply] = useState(false);
  const [communicationPolicy, setCommunicationPolicy] = useState(
    initialCommunicationPolicy,
  );
  const [socket, setSocket] = useState(null);
  const [aiPausedUntil, setAiPausedUntil] = useState(null);
  const aiPauseExpiresAt = aiPausedUntil
    ? new Date(aiPausedUntil).getTime()
    : NaN;
  const isAiPaused = aiPauseExpiresAt > Date.now();
  // null = AI idle, '' = thinking (no tokens yet), 'text...' = streaming
  const [streamingText, setStreamingText] = useState(null);
  const { hotel } = useFetchHotel();
  const messagingLocked = !getOfferCapabilities(hotel).messaging;
  const { currentUser } = useSetCurrentUser();
  const { t } = useTranslation();
  const threadKey = JSON.stringify([hotel?._id, bookingId, bookingIds || []]);
  const historyScope = useRef({ threadKey });
  const historyRequest = useRef(0);
  if (historyScope.current.threadKey !== threadKey) {
    historyScope.current = { threadKey };
  }

  const messageEndRef = useRef(null);
  const messageContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const hasInitialScrollRef = useRef(false);

  const isNearBottom = useCallback(() => {
    const container = messageContainerRef.current;
    if (!container) return true;
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight < 80
    );
  }, []);

  const scrollToBottom = useCallback(
    (force = false) => {
      if ((force || isNearBottom()) && messageEndRef.current)
        messageEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
    },
    [isNearBottom],
  );

  const markLastMessagesAsRead = useCallback(
    async (pMessages) => {
      const readerId = currentUser?._id || currentUser?.id;
      const unread = pMessages.filter((message) => {
        const authorId = message.createdBy?._id || message.createdBy;
        return (
          !isMessageLocked(message, messagingLocked) && ["SENT", "DELIVERED"].includes(message.status) &&
          (message.internal
            ? readerId && String(authorId) !== String(readerId)
            : !message.fromHotel)
        );
      });
      const latest = new Map();
      for (const message of unread) {
        const key = `${message.booking}:${Boolean(message.internal)}`;
        const previous = latest.get(key);
        if (
          !previous ||
          new Date(message.createdAt) > new Date(previous.createdAt)
        )
          latest.set(key, message);
      }
      for (const message of latest.values()) {
        const response = await CommunicationService.markAsRead(message._id);
        if (response.data?.markedRead === false) continue;
        setMessages((previous) =>
          previous.map((entry) =>
            entry._id === message._id ? { ...entry, status: "READ" } : entry,
          ),
        );
        if (!message.internal) {
          socket?.emit("messageRead", message);
          onMessagesRead?.(conversationId || bookingId);
        }
      }
    },
    [
      bookingId,
      conversationId,
      onMessagesRead,
      messagingLocked,
      socket,
      currentUser?._id,
      currentUser?.id,
    ],
  );

  const loadMessages = useCallback(async () => {
    if (!bookingId) return;
    const scope = historyScope.current;
    if (scope.threadKey !== threadKey) return;
    const requestId = ++historyRequest.current;
    try {
      const response = await CommunicationService.getMessages(
        bookingId,
        undefined,
        bookingIds,
      );
      if (
        historyScope.current !== scope ||
        historyRequest.current !== requestId
      )
        return;
      if (response.data) {
        const { messages: nextMessages, hasMoreMessages: nextHasMoreMessage } =
          response.data;
        setMessages(nextMessages);
        setHasMoreMessages(nextHasMoreMessage);
        setCommunicationPolicy(response.data.communicationPolicy);
        setAiPausedUntil(response.data.aiPausedUntil || null);
        if (bookingIds?.length > 1) {
          // Check the reservation independently: the visible history spans multiple stays.
          CommunicationService.getMessages(bookingId).then(({ data }) => {
            if (historyScope.current === scope && historyRequest.current === requestId) setDraftContext(data);
          }).catch(() => { /* Leave drafting available when eligibility cannot be determined. */ });
        } else setDraftContext(response.data);
        await Promise.all(
          (bookingIds?.length ? bookingIds : [bookingId]).map((id) =>
            NotificationService.removeMessage(id),
          ),
        );
        setTimeout(() => {
          if (
            historyScope.current !== scope ||
            historyRequest.current !== requestId
          )
            return;
          if (!hasInitialScrollRef.current) {
            hasInitialScrollRef.current = true;
            scrollToBottom(true);
          }
          markLastMessagesAsRead(nextMessages);
        }, 1000);
      }
    } catch (error) {
      if (
        historyScope.current === scope &&
        historyRequest.current === requestId
      ) {
        presentToastErrorContent(error);
      }
    }
  }, [
    bookingId,
    bookingIds,
    threadKey,
    markLastMessagesAsRead,
    scrollToBottom,
  ]);

  const loadEarlierMessages = async () => {
    if (!bookingId) return;
    const scope = historyScope.current;
    const requestId = historyRequest.current;
    setIsLoadingEarlierMessages(true);
    try {
      const { data } = await CommunicationService.getMessages(
        bookingId,
        messages[0]._id,
        bookingIds,
      );
      if (
        historyScope.current !== scope ||
        historyRequest.current !== requestId
      )
        return;
      const { messages: newMessages, hasMoreMessages: nextHasMoreMessage } =
        data;
      setMessages((prevMessages) => [...newMessages, ...prevMessages]);
      setHasMoreMessages(nextHasMoreMessage);
      setCommunicationPolicy(data.communicationPolicy);
      await markLastMessagesAsRead(newMessages);
    } catch (error) {
      if (
        historyScope.current === scope &&
        historyRequest.current === requestId
      ) {
        presentToastErrorContent(error);
      }
    } finally {
      if (historyScope.current === scope) setIsLoadingEarlierMessages(false);
    }
  };

  useEffect(() => {
    if (!aiPausedUntil) return undefined;
    let expiryTimer;
    const expirePause = () => {
      const remaining = aiPauseExpiresAt - Date.now();
      if (!Number.isFinite(remaining) || remaining <= 0) {
        setAiPausedUntil(null);
        return;
      }
      expiryTimer = setTimeout(expirePause, Math.min(remaining, 2147483647));
    };
    expirePause();
    return () => clearTimeout(expiryTimer);
  }, [aiPausedUntil, aiPauseExpiresAt]);

  // Seed a new thread with the latest policy without resetting its history
  // whenever the parent supplies an updated policy object.
  const initialCommunicationPolicyRef = useRef(initialCommunicationPolicy);
  useEffect(() => {
    initialCommunicationPolicyRef.current = initialCommunicationPolicy;
  }, [initialCommunicationPolicy]);

  useEffect(() => {
    if (historyScope.current.threadKey !== threadKey) {
      historyScope.current = { threadKey };
    }
    const scope = historyScope.current;
    hasInitialScrollRef.current = false;
    setMessages([]);
    setHasMoreMessages(false);
    setIsLoadingEarlierMessages(false);
    setStreamingText(null);
    setInternalReply(false);
    setPhoto(null);
    composerVersion.current += 1;
    setCurrentMessage("");
    setDrafting(false);
    setDraftError(false);
    setDraftContext(null);
    setCommunicationPolicy(initialCommunicationPolicyRef.current);
    setAiPausedUntil(null);
    return () => {
      if (historyScope.current === scope)
        historyScope.current = { threadKey: null };
    };
  }, [threadKey]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!externalMessage) return;
    const shouldScroll = isNearBottom();
    setMessages((currentMessages) =>
      currentMessages.some(
        (message) => String(message._id) === String(externalMessage._id),
      )
        ? currentMessages
        : [...currentMessages, externalMessage],
    );
    if (shouldScroll) setTimeout(() => scrollToBottom(true), 50);
  }, [externalMessage, isNearBottom, scrollToBottom]);

  useEffect(() => {
    const newSocket = io(SERVER_URL, {
      auth: (done) => done({ protocol: 2, headers: authHeader() }), transports: ["websocket"],
    });

    newSocket.on("connect_error", (error) => {
      console.error("Socket error:", error);
    });

    newSocket.on("connect", () => {
      console.log("Connected to server");
      setConnectedToSocket(true);
      if (hotel?._id) newSocket.emit("joinHotel", { hotelId: hotel._id });
      (bookingIds?.length ? bookingIds : [bookingId]).forEach((id) =>
        newSocket.emit("join", { bookingId: id, isHotel: true }),
      );
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [bookingId, bookingIds, hotel?._id]);

  useEffect(() => {
    if (socket) {
      socket.on("receiveMessage", async (message) => {
        if (message.isTestGuest) return;
        if (message.internal) {
          const shouldScroll = isNearBottom();
          setMessages((prevMessages) =>
            prevMessages.some(
              (existingMessage) => existingMessage._id === message._id,
            )
              ? prevMessages
              : [...prevMessages, message],
          );
          if (shouldScroll) setTimeout(() => scrollToBottom(true), 50);
          await markLastMessagesAsRead([message]);
        } else if (!message.fromHotel) {
          const shouldScroll = isNearBottom();
          setMessages((prevMessages) => [...prevMessages, message]);

          const response = await CommunicationService.markAsRead(message._id);
          if (response.data?.markedRead === false) {
            if (shouldScroll) setTimeout(() => scrollToBottom(true), 500);
            return;
          }
          onMessagesRead?.(conversationId || bookingId);
          socket.emit("messageRead", message);
          if (shouldScroll) setTimeout(() => scrollToBottom(true), 500);
        } else if (message.fromAI) {
          // AI responded to the guest — show in hotel’s view and clear streaming indicator
          setStreamingText(null);
          const shouldScroll = isNearBottom();
          setMessages((prevMessages) => [...prevMessages, message]);
          if (shouldScroll) setTimeout(() => scrollToBottom(true), 500);
        }
      });

      socket.on("aiStart", () => {
        setStreamingText("");
        setTimeout(scrollToBottom, 100);
      });
      socket.on("aiToken", ({ text }) => {
        setStreamingText((prev) => (prev === null ? text : prev + text));
      });
      socket.on("aiError", () => {
        setStreamingText(null);
      });

      socket.on("onUsersChange", async (onlineStatuses) => {
        setIsGuestOnline(onlineStatuses?.guest);
      });

      socket.on("messageRead", (message) => {
        setMessages((prevMessages) =>
          prevMessages.map((m) =>
            String(m.booking) === String(message.booking || bookingId) &&
            Boolean(m.internal) === Boolean(message.internal) &&
            m.fromHotel === Boolean(message.fromHotel) &&
            (!message.internal ||
              String(m.createdBy?._id || m.createdBy) !== message.readerId) &&
            (m._id === message._id ||
              !message.readAt ||
              new Date(m.createdAt) < new Date(message.readAt)) &&
            m.status !== "READ"
              ? { ...m, status: "READ" }
              : m,
          ),
        );
      });

      socket.on("messageStatus", (message) => {
        if (!message.fromHotel) return;

        setMessages((prevMessages) => {
          const index = prevMessages.findIndex((m) => m._id === message._id);
          if (index !== -1) {
            const nextMessages = [...prevMessages];
            nextMessages[index] = {
              ...nextMessages[index],
              status: message.status,
              errorCode: message.errorCode,
              errorMessage: message.errorMessage,
            };
            return nextMessages;
          }
          return prevMessages;
        });
      });

      socket.on("internalChatChanged", ({ bookingId: changedBookingId }) => {
        if (
          (bookingIds?.length ? bookingIds : [bookingId]).includes(
            changedBookingId,
          )
        )
          loadMessages();
      });
      return () =>
        [
          "receiveMessage",
          "aiStart",
          "aiToken",
          "aiError",
          "onUsersChange",
          "messageRead",
          "messageStatus",
          "internalChatChanged",
        ].forEach((event) => socket.off(event));
    }
  }, [
    bookingId,
    conversationId,
    isNearBottom,
    onMessagesRead,
    scrollToBottom,
    socket,
    loadMessages,
    markLastMessagesAsRead,
    bookingIds,
  ]);

  const handleHandoffToAi = async () => {
    try {
      await CommunicationService.handoffToAi(bookingId);
      setAiPausedUntil(null);
    } catch (error) {
      presentToastErrorContent(error);
    }
  };

  const handleSendMessage = async () => {
    if (messagingLocked && !internalReply) { setUpgrade("messaging"); return; }
    if (
      (!currentMessage?.trim() && !photo) ||
      isSending ||
      (!internalReply && communicationPolicy?.hotel.canSend === false)
    )
      return;
    const trimmed = currentMessage.trim();
    const pendingId = "pending_" + Date.now();
    const pendingMessage = {
      _id: pendingId,
      booking: bookingId,
      internal: internalReply,
      createdBy: currentUser?._id || currentUser?.id,
      fromHotel: true,
      fromAI: false,
      text: trimmed,
      status: "SENDING",
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, pendingMessage]);
    composerVersion.current += 1;
    setCurrentMessage("");
    if (messageInputRef.current) messageInputRef.current.style.height = "40px";
    setIsSending(true);
    setTimeout(() => scrollToBottom(true), 100);
    try {
      const { data: newMessage } = internalReply
        ? await CommunicationService.notifyStaff(
            bookingId,
            { type: "conversation" },
            trimmed,
            photo,
          )
        : await CommunicationService.sendMessage(bookingId, trimmed, {
            forceSms: smsUnreachable && forceSms,
            photo,
          });
      if (newMessage) {
        setPhoto(null);
        setMessages((prev) =>
          prev.map((m) => (m._id === pendingId ? newMessage : m)),
        );
        if (!newMessage.internal) socket?.emit("sendMessage", newMessage);
        onMessageSent?.(newMessage);
        await loadMessages();
      }
    } catch (error) {
      setMessages((prev) =>
        prev.map((m) => (m._id === pendingId ? { ...m, status: "ERROR" } : m)),
      );
      if (error.response?.data?.communicationPolicy) {
        setCommunicationPolicy(error.response.data.communicationPolicy);
      }
      setCurrentMessage(previous => previous || trimmed);
      if (error.response?.data?.code === "MESSAGING_REQUIRED") setUpgrade("messaging");
      else presentToastErrorContent(error);
    } finally {
      setIsSending(false);
    }
  };

  const timelineItems = [
    ...messages,
    ...reservations.flatMap((reservation) =>
      [
        reservation.checkinDate && {
          _id: `checkin-${reservation._id}`,
          createdAt: reservation.checkinDate,
          timelineEvent: "checkin",
          reservationNumber: reservation.reservationNumber,
          roomNumber: reservation.roomNumber,
        },
        reservation.checkoutDate && {
          _id: `checkout-${reservation._id}`,
          createdAt: reservation.checkoutDate,
          timelineEvent: "checkout",
          reservationNumber: reservation.reservationNumber,
          roomNumber: reservation.roomNumber,
        },
      ].filter((event) => event && new Date(event.createdAt).getTime() <= Date.now()),
    ),
  ].sort((left, right) => new Date(left.createdAt) - new Date(right.createdAt));

  // The endpoint drafts for this reservation, not the merged history of older stays.
  const scopedBookingIds = new Set([String(bookingId), ...(draftContext?.messages || []).map(message => String(message.booking?._id || message.booking))]);
  const latestGuestMessage = [...messages.filter(message => scopedBookingIds.has(String(message.booking?._id || message.booking))), ...(draftContext?.messages || [])]
    .filter(message => !message.internal && !message.fromHotel && !message.fromAI)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  const draftUnavailable = communicationPolicy?.hotel.canSend === false
    ? "replyDraft.closed"
    : getOfferCapabilities(hotel).ai && !latestGuestMessage?.text?.trim() && (latestGuestMessage || draftContext?.hasMoreMessages === false)
      ? "replyDraft.noGuestMessage"
      : null;

  return (
    <>
      {!embedded && (
        <Box direction="row" justify="between" margin={{ bottom: "small" }}>
          <Text weight="bold" size="medium" direction="row">
            {guestName}{" "}
            {isGuestOnline ? (
              <Text color="status-ok">(Online)</Text>
            ) : (
              <Text color="status-warning">(SMS)</Text>
            )}
          </Text>
          {!connectedToSocket && (
            <Button
              label={t("conversation.refresh")}
              onClick={() => loadMessages()}
              primary
              color="brand"
              alignSelf="end"
            />
          )}
        </Box>
      )}
      <Box gap="none" width={embedded ? "100%" : "large"} fill={embedded}>
        <div
          className={`chat-wrapper${embedded ? " chat-wrapper--embedded" : ""}`}
        >
          <div className="chat-container" ref={messageContainerRef}>
            <div>
              {hasMoreMessages && (
                <Box align="center" margin={{ bottom: "small" }}>
                  <button
                    className="chat-load-more-btn"
                    onClick={
                      !isLoadingEarlierMessages
                        ? loadEarlierMessages
                        : undefined
                    }
                    disabled={isLoadingEarlierMessages}
                  >
                    {isLoadingEarlierMessages ? (
                      <Spinner size="xsmall" />
                    ) : (
                      t("conversation.loadEarlier")
                    )}
                  </button>
                </Box>
              )}
              {timelineItems.map((message, index) => {
                if (message.timelineEvent) {
                  return (
                    <div className="chat-lifecycle-separator" key={message._id}>
                      <span>
                        {t(
                          message.timelineEvent === "checkin"
                            ? "conversation.checkedIn"
                            : "conversation.checkedOut",
                        )}
                      </span>
                      <strong>
                        {moment
                          .tz(message.createdAt, hotel.timezone)
                          .format("lll")}
                      </strong>
                      <small>
                        {t("conversation.reservationContext", {
                          room: message.roomNumber || "-",
                          reservation: message.reservationNumber || "-",
                        })}
                      </small>
                    </div>
                  );
                }
                const isHotel = message.fromHotel && !message.fromAI;
                const isAI = message.fromAI;
                const isSystem = message.system;
                const isInternal = message.internal;
                const isGuest = !message.fromHotel && !message.fromAI;
                const contentLocked = isMessageLocked(message, messagingLocked);

                // Day separator logic
                const msgDate = moment.tz(message.createdAt, hotel.timezone);
                const prevMsg = index > 0 ? timelineItems[index - 1] : null;
                const prevDate = prevMsg
                  ? moment.tz(prevMsg.createdAt, hotel.timezone)
                  : null;
                const showDaySeparator =
                  !prevDate || !msgDate.isSame(prevDate, "day");

                // Smart time: time-only if today, otherwise day + date + time
                const isToday = msgDate.isSame(
                  moment().tz(hotel.timezone),
                  "day",
                );
                const timeDisplay = isToday
                  ? msgDate.format("LT")
                  : msgDate.format("ddd, MMM D \u00b7 LT");

                let bubbleClass = "chat-bubble ";
                let senderClass = "chat-sender ";
                let timeClass = "chat-time ";
                let rowClass = "chat-message-row ";

                if (isInternal) {
                  bubbleClass += "chat-bubble--internal";
                  senderClass += "chat-sender--internal";
                  timeClass += "chat-time--internal";
                  rowClass += "chat-message-row--internal";
                } else if (isHotel) {
                  bubbleClass += "chat-bubble--hotel";
                  senderClass += "chat-sender--hotel";
                  timeClass += "chat-time--hotel";
                  rowClass += "chat-message-row--hotel";
                } else if (isSystem) {
                  bubbleClass += "chat-bubble--system";
                  senderClass += "chat-sender--system";
                  timeClass += "chat-time--hotel";
                  rowClass += "chat-message-row--hotel";
                } else if (isAI) {
                  bubbleClass += "chat-bubble--ai";
                  senderClass += "chat-sender--ai";
                  timeClass += "chat-time--hotel";
                  rowClass += "chat-message-row--hotel";
                } else {
                  bubbleClass += "chat-bubble--guest";
                  senderClass += "chat-sender--guest";
                  timeClass += "chat-time--other";
                  rowClass += "chat-message-row--other";
                }

                if (message.status === "SENDING") {
                  bubbleClass += " chat-bubble--sending";
                } else if (message.status === "ERROR") {
                  bubbleClass += " chat-bubble--error";
                }

                return (
                  <React.Fragment key={message._id}>
                    {showDaySeparator && (
                      <div className="chat-date-separator">
                        <div className="chat-date-separator__line" />
                        <span className="chat-date-separator__text">
                          {isToday
                            ? t("conversation.today")
                            : msgDate.isSame(
                                  moment()
                                    .tz(hotel.timezone)
                                    .subtract(1, "day"),
                                  "day",
                                )
                              ? t("conversation.yesterday")
                              : msgDate.format("ddd, MMM D")}
                        </span>
                        <div className="chat-date-separator__line" />
                      </div>
                    )}
                    <div className={rowClass}>
                      <div className={bubbleClass}>
                        {/* Sender label */}
                        {isSystem && (
                          <div className={senderClass}>
                            🔔 {t("conversation.aiAssistant")}
                          </div>
                        )}
                        {isInternal && (
                          <div className="chat-internal-header">
                            <div className={senderClass}>
                              {t("conversation.internalOnly")}
                            </div>
                            <span>
                              {message.createdBy?.username ||
                                t("conversation.staffMember")}
                            </span>
                          </div>
                        )}
                        {isAI && !isSystem && (
                          <div className={senderClass}>
                            {t("conversation.aiAssistant")}
                          </div>
                        )}
                        {isHotel && !isInternal && (
                          <div className={senderClass}>
                            {t("conversation.you")}
                          </div>
                        )}
                        {isGuest && (
                          <div className={senderClass}>{guestName}</div>
                        )}

                        {contentLocked && (
                          <button type="button" className="chat-locked-message" onClick={() => setUpgrade("messaging")} aria-label={t("offers.unlockMessaging")}>
                            <span aria-hidden="true" className="chat-locked-placeholder">•••••••••• ••••••• ••••••••••</span>
                            <span>{t("offers.unlockMessaging")}</span>
                          </button>
                        )}
                        {!contentLocked && message.photo && (
                          <SecuredImage
                            className="chat-photo"
                            filename={message.photo}
                          />
                        )}

                        {!contentLocked && message.text &&
                          (isAI || message.fromHotel ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: renderAIHtml(message.text),
                              }}
                            />
                          ) : (
                            <div>{message.text}</div>
                          ))}

                        <div className={timeClass}>
                          {message.status === "SENDING" ? (
                            <Spinner size="xsmall" />
                          ) : message.status === "ERROR" ? (
                            <span>✕ {t("conversation.failed")}</span>
                          ) : (
                            <>
                              {timeDisplay}
                              {isInternal &&
                                ` · ${t(`conversation.${message.status.toLowerCase()}`)}`}
                              {isHotel &&
                                !isInternal &&
                                message.status === "READ" &&
                                " \u2713\u2713"}
                              {isHotel &&
                                !isInternal &&
                                message.status !== "READ" &&
                                ` \u2022 ${message.status.toLowerCase()}`}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            {streamingText !== null && (
              <div className="chat-bubble chat-bubble--ai chat-bubble--streaming">
                <div className="chat-sender chat-sender--ai">
                  {t("conversation.aiAssistant")}
                </div>
                {streamingText === "" ? (
                  <div className="chat-typing-dots">
                    <span />
                    <span />
                    <span />
                  </div>
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: renderAIHtml(streamingText),
                    }}
                  />
                )}
              </div>
            )}
            <div ref={messageEndRef}></div>
          </div>
          <div className="chat-input-area guest-message-composer">
            <CheckBox
              checked={internalReply}
              onChange={(event) => {
                composerVersion.current += 1;
                setInternalReply(event.target.checked);
              }}
              label={t("conversation.internalOnly")}
            />
            {internalReply && <p className="chat-internal-note-help">{t("conversation.internalNoteHelp")}</p>}
            {hotel?.aiEnabled && hotel?.aiGuestAssistantEnabled && isAiPaused && (
              <div className="chat-policy-banner chat-ai-pause">
                <span>
                  {t("conversation.aiPausedUntil", {
                    time: moment(aiPausedUntil).tz(hotel.timezone).format("LT"),
                  })}
                </span>
                <button type="button" onClick={handleHandoffToAi}>
                  {t("conversation.handoffToAi")}
                </button>
              </div>
            )}
            {communicationPolicy && communicationPolicy.conversation.lifecycle !== "not_started" && (
              <div
                className={`chat-policy-banner chat-policy-banner--${communicationPolicy.conversation.lifecycle}`}
              >
                {communicationPolicy.conversation.lifecycle === "archived"
                  ? t("conversation.policyArchived")
                  : communicationPolicy.conversation.lifecycle ===
                      "reply_window_expired"
                    ? t("conversation.policyClosed")
                    : communicationPolicy.conversation.lifecycle === "open"
                      ? t("conversation.policyOpen")
                      : t("conversation.policyNotStarted")}
              </div>
            )}
            {smsUnreachable && (
              <Box
                pad="small"
                margin={{ bottom: "small" }}
                round="small"
                background={
                  email && !emailUnreachable
                    ? "status-warning"
                    : "status-critical"
                }
                gap="xsmall"
              >
                <Text size="small" color="white" weight="bold">
                  {email && !emailUnreachable
                    ? t("comms.convBannerSmsOnlyEmailFallback", {
                        phone: phone || t("comms.bannerThisNumber"),
                      })
                    : t("comms.convBannerSmsAndNoEmail", {
                        phone: phone || t("comms.bannerThisNumber"),
                      })}
                </Text>
                {phone && (
                  <CheckBox
                    checked={forceSms}
                    onChange={(e) => setForceSms(e.target.checked)}
                    label={
                      <Text size="small" color="white">
                        {t("comms.convForceRetryCheckbox")}
                      </Text>
                    }
                  />
                )}
              </Box>
            )}
            <div className="chat-composer-box">
            <textarea
              ref={messageInputRef}
              rows={1}
              value={currentMessage}
              disabled={(!internalReply && (messagingLocked || communicationPolicy?.hotel.canSend === false))}
              onChange={(event) => {
                composerVersion.current += 1;
                setCurrentMessage(event.target.value);
                event.target.style.height = "auto";
                event.target.style.height = `${Math.min(
                  event.target.scrollHeight,
                  120,
                )}px`;
              }}
              placeholder={
                (!internalReply && communicationPolicy?.hotel.canSend === false)
                  ? t("conversation.archivedPlaceholder")
                  : t("conversation.placeholder")
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <div className="chat-composer-toolbar">
              <div className="chat-composer-tools">
            <MessagePhotoInput value={photo} onChange={setPhoto} disabled={isSending || (!internalReply && (messagingLocked || communicationPolicy?.hotel.canSend === false))} />
            {!internalReply && !messagingLocked && <Popover className="chat-draft-popover" preferPlace="above" tipSize={6} isOpen={Boolean(draftUnavailable) && draftHelpOpen}
              body={<div id="chat-draft-reason" role="tooltip" className="chat-draft-tooltip">{draftUnavailable ? t(draftUnavailable) : null}</div>}>
              <span className="chat-draft-trigger" tabIndex={draftUnavailable ? 0 : undefined}
                aria-label={draftUnavailable ? t("replyDraft.action") : undefined}
                aria-describedby={draftUnavailable && draftHelpOpen ? "chat-draft-reason" : undefined}
                onMouseEnter={() => setDraftHelpOpen(true)} onMouseLeave={() => setDraftHelpOpen(false)}
                onFocus={() => setDraftHelpOpen(true)} onBlur={() => setDraftHelpOpen(false)}
                onClick={() => { if (draftUnavailable) setDraftHelpOpen(true); }}
                onKeyDown={event => { if (event.key === "Escape") setDraftHelpOpen(false); }}>
              <Button label={t(drafting ? "replyDraft.loading" : "replyDraft.action")} className="chat-draft-action" disabled={drafting || isSending || Boolean(draftUnavailable)} onClick={async () => {
              if (!getOfferCapabilities(hotel).ai) { setUpgrade(true); return; }
              if (currentMessage.trim() && !window.confirm(t("replyDraft.replace"))) return;
              const draftScope = historyScope.current;
              const draftVersion = composerVersion.current;
              setDrafting(true);
              setDraftError(false);
              try {
                const { data } = await CommunicationService.draftReply(bookingId);
                if (historyScope.current === draftScope && composerVersion.current === draftVersion) setCurrentMessage(data.text);
              } catch (error) {
                if (historyScope.current === draftScope) {
                  const data = error.response?.data;
                  if (data?.code === "MESSAGING_REQUIRED") setUpgrade("messaging");
                  else if ((data?.code === "OFFER_CAPABILITY_REQUIRED" && data.capability === "ai") || data?.message === "AI & Upsells package required.") setUpgrade("ai");
                  else { setDraftError(true); presentToastErrorContent(error); }
                }
              }
              finally { if (historyScope.current === draftScope) setDrafting(false); }
            }} /></span></Popover>}
            {upgrade && <PackageUpgradeModal capability={upgrade === "messaging" ? "messaging" : "ai"} onClose={() => setUpgrade(false)} />}
              </div>
            <Button
              label={
                messagingLocked && !internalReply ? t("offers.unlockMessaging") : isSending ? t("conversation.sending") : t("conversation.send")
              }
              onClick={handleSendMessage}
              primary
              disabled={
                isSending || (!internalReply && communicationPolicy?.hotel.canSend === false)
              }
            />
            </div>
            </div>
            {!internalReply && draftError && !draftUnavailable && (
              <p className="chat-composer-help" role="alert">
                {t("replyDraft.failed")}
              </p>
            )}
          </div>
        </div>
      </Box>
    </>
  );
}
